import type { ReactNode } from 'react'

import { FvNum } from '@fv/client-components'
import type { ExchangeRateMap, MarkupSettings } from '@fv/models'
import { type DTO, quoteUtils } from '@fv/models/core'

import { AdminBadge } from '@/components/shared/AdminBadge'
import { AdminDate } from '@/components/shared/AdminDate'
import type { AdminQuote } from '@/types/admin-load'

import { QuoteHistoryTable } from './QuoteHistoryTable'

interface QuoteDetailsPanelProps {
  quote: DTO<AdminQuote>
  markup?: MarkupSettings
  exchangeRates?: ExchangeRateMap
}

interface DetailRowProps {
  label: string
  value?: ReactNode | boolean
  hide?: boolean
}

const DetailRow = ({ label, value, hide = false }: DetailRowProps) => {
  if (typeof value === 'boolean') {
    value = value ? 'Yes' : 'No'
  }

  if (hide) return null

  return (
    <>
      <div className="font-medium">{label}:</div>
      <div>{value}</div>
    </>
  )
}

interface DetailSectionProps {
  title: string
  children: ReactNode
  titleClassName?: string
}

const DetailSection = ({
  title,
  children,
  titleClassName,
}: DetailSectionProps) => {
  return (
    <div className="space-y-3">
      <h3
        className={`text-lg font-semibold border-b pb-2 ${titleClassName || ''}`}
      >
        {title}
      </h3>
      {children}
    </div>
  )
}

export const QuoteDetailsPanel = ({
  quote,
  markup,
  exchangeRates,
}: QuoteDetailsPanelProps) => {
  const markupCalc = markup
    ? quoteUtils.calculateMarkup(quote, markup, exchangeRates)
    : undefined
  const hasMarkup = markupCalc?.charge && markupCalc.method !== 'none'
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <DetailSection title="Basic Information">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <DetailRow label="Quote Number" value={quote.quoteNum || 'N/A'} />
            <DetailRow
              label="Carrier"
              value={quote.assetCarrierName || 'N/A'}
            />
            <DetailRow label="Provider" value={quote.providerName} />
            <DetailRow
              label="Status"
              value={
                <AdminBadge
                  className={`badge-sm ${quote.status === 'active' ? 'badge-success' : quote.status === 'error' ? 'badge-error' : 'badge-primary'}`}
                >
                  {quote.status}
                </AdminBadge>
              }
            />
            <DetailRow label="Source" value={quote.source} />
            <DetailRow
              label="Method"
              value={`${quote.method} ${quote.scrape ? '(scrape)' : ''}`}
            />
          </div>
        </DetailSection>

        <DetailSection title="Pricing">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <DetailRow
              label="Total Amount"
              value={<FvNum currency={quote.currency} val={quote.amount} />}
            />
            <DetailRow label="Currency" value={quote.currency?.toUpperCase()} />
            <DetailRow label="Pricing Method" value={quote.pricingMethod} />
            <DetailRow label="Pricing Type" value={quote.pricingType} />
            <DetailRow
              label="Pricing Description"
              value={quote.pricingDescription}
              hide={!quote.pricingDescription}
            />
            <DetailRow
              label="Markup"
              value={
                hasMarkup && (
                  <>
                    <FvNum
                      currency={markupCalc.charge.currency}
                      val={markupCalc.charge.amount}
                    />
                    <br />({quoteUtils.describeMarkupSettings(markup)})
                  </>
                )
              }
              hide={!markupCalc || markupCalc.method === 'none'}
            />
            <DetailRow
              label="Markup Total"
              value={
                hasMarkup && (
                  <FvNum
                    currency={markupCalc.charge.currency}
                    val={markupCalc.amount}
                  />
                )
              }
              hide={!markupCalc || markupCalc.method === 'none'}
            />
          </div>
        </DetailSection>
      </div>

      <DetailSection title="Service Details">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <DetailRow label="Equipment Type" value={quote.equipmentType} />
            <DetailRow label="Mode" value={quote.mode} />
            <DetailRow
              label="Service Description"
              value={quote.serviceDescription}
              hide={!quote.serviceDescription}
            />
            <DetailRow
              label="Payment Terms"
              value={quote.paymentTerms}
              hide={!quote.paymentTerms}
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <DetailRow label="Guaranteed" value={!!quote.guaranteed} />
            <DetailRow label="Interline" value={!!quote.interline} />
            <DetailRow label="Spot Quote" value={!!quote.isSpot} />
            <DetailRow label="End of Day" value={!!quote.endOfDay} />
          </div>
        </div>
      </DetailSection>

      <DetailSection title="Dates & Transit">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <DetailRow
              label="Created Date"
              value={<AdminDate val={quote.createdDate} />}
            />
            <DetailRow
              label="Pickup Date"
              value={<AdminDate val={quote.pickupDate} />}
              hide={!quote.pickupDate}
            />
            <DetailRow
              label="Delivery Date"
              value={<AdminDate val={quote.deliveryDate} />}
              hide={!quote.deliveryDate}
            />
            <DetailRow
              label="Expiration Date"
              value={<AdminDate val={quote.expirationDate} />}
              hide={!quote.expirationDate}
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <DetailRow
              label="Transit Days"
              value={
                quote.transitDaysMin === quote.transitDaysMax
                  ? `${quote.transitDaysMin} days`
                  : `${quote.transitDaysMin || '?'} - ${quote.transitDaysMax || '?'} days`
              }
              hide={!(quote.transitDaysMin || quote.transitDaysMax)}
            />
          </div>
        </div>
      </DetailSection>

      {quote.charges && quote.charges.length > 0 && (
        <DetailSection title="Charges">
          <div className="overflow-x-auto">
            <table className="table table-sm w-full">
              <thead>
                <tr>
                  <th>Charge Name</th>
                  <th>Amount</th>
                  <th>Code</th>
                  <th>Currency</th>
                  <th>Provider Detail</th>
                </tr>
              </thead>
              <tbody>
                {quote.charges.map((charge, index) => (
                  <tr key={index}>
                    <td>{charge.name}</td>
                    <td>
                      <FvNum currency={charge.currency} val={charge.amount} />
                    </td>
                    <td>{charge.code || 'N/A'}</td>
                    <td>{charge.currency?.toUpperCase()}</td>
                    <td>{charge.providerDetail || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DetailSection>
      )}

      {quote.refNums && quote.refNums.length > 0 && (
        <DetailSection title="Reference Numbers">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            {quote.refNums.map((refNum, index) => (
              <div key={index} className="grid grid-cols-2 gap-2">
                <DetailRow label={refNum.type} value={refNum.value} />
              </div>
            ))}
          </div>
        </DetailSection>
      )}

      {quote.notes && quote.notes.length > 0 && (
        <DetailSection title="Notes">
          <ul className="list-disc list-inside space-y-1 text-sm">
            {quote.notes.map((note, index) => (
              <li key={index}>{note}</li>
            ))}
          </ul>
        </DetailSection>
      )}

      {quote.errors && quote.errors.length > 0 && (
        <DetailSection title="Errors" titleClassName="text-error">
          <div className="space-y-2">
            {quote.errors.map((error, index) => (
              <div key={index} className="alert alert-error text-sm">
                <div>
                  <strong className="capitalize">{error.type}:</strong>{' '}
                  {error.message}
                </div>
              </div>
            ))}
          </div>
        </DetailSection>
      )}

      <DetailSection title="Additional Information">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <DetailRow
              label="Account Number"
              value={quote.accountNumber}
              hide={!quote.accountNumber}
            />
            <DetailRow
              label="Carrier Code"
              value={quote.assetCarrierCode}
              hide={!quote.assetCarrierCode}
            />
            <DetailRow
              label="Provider Code"
              value={quote.providerCode}
              hide={!quote.providerCode}
            />
            <DetailRow label="Latency" value={`${quote.time || 0} ms`} />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <DetailRow label="Service ID" value={quote.serviceId} />
            <DetailRow
              label="Load Quote Version"
              value={quote.loadQuoteVersion}
              hide={!quote.loadQuoteVersion}
            />
          </div>
        </div>
      </DetailSection>

      {quote.quoteHistory && quote.quoteHistory.length > 0 && (
        <DetailSection title="Quote History">
          <QuoteHistoryTable history={quote.quoteHistory} />
        </DetailSection>
      )}
    </div>
  )
}
